import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "content" }

import useNewSms from '@/hooks/use-new-sms';
import { AsyncComponents } from '@/components';
import { getService } from '@/api/user/index';
import { Content, Main } from '@/layout';
import { engageLabCore } from '@jg/qiankun-micro-tool';
import { ref, provide, reactive, watch, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { getUserPermission } from '@/api/user';
import { ConfigProvider, theme } from 'ant-design-vue';
import type { ThemeConfig } from 'ant-design-vue/es/config-provider/context';
import { liveDeskAuth } from '@/api/livedesk';
import en_US from 'ant-design-vue/es/locale/en_US';
import zh_CN from 'ant-design-vue/es/locale/zh_CN';
import zh_TW from 'ant-design-vue/es/locale/zh_TW';
import ja_JP from 'ant-design-vue/es/locale/ja_JP';
import th_TH from 'ant-design-vue/es/locale/th_TH';
import es_ES from 'ant-design-vue/es/locale/es_ES';
import id_ID from 'ant-design-vue/es/locale/id_ID';
import { getCookie } from '@/utils/cookie';
import { defCurrentData } from '@/api/user/type';
import report from '@/utils/escalation';

export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const locale = engageLabCore.locale.takeImmediately();

// Ant Design Vue 4.x 主题配置 (从 Less 变量迁移)
const themeConfig: ThemeConfig = {
  token: {
    // 主色系
    colorPrimary: '#307dff',
    colorPrimaryHover: '#5795ff',
    colorPrimaryActive: '#0054e1',
    // 功能色
    colorSuccess: '#23b541',
    colorError: '#e42f2f',
    colorWarning: '#f29217',
    // 文字色
    colorText: 'rgba(37, 48, 68, 0.84)',
    colorTextHeading: 'rgba(37, 48, 68, 1)',
    colorTextDisabled: 'rgba(37, 48, 68, 0.32)',
    colorLink: '#307dff',
    // 边框和背景
    colorBorder: 'rgba(37, 48, 68, 0.12)',
    colorBgContainer: '#fff',
    colorBgLayout: '#f0f3f7',
    // 圆角
    borderRadius: 6,
    borderRadiusSM: 4,
    // 字体大小
    fontSizeLG: 14,
  },
  components: {
    Table: {
      headerBg: '#f6f7fa',
      headerColor: '#253044',
      rowHoverBg: '#f0f3f7',
    },
    Modal: {
      headerBorderColor: 'transparent',
    },
    Button: {
      primaryColor: '#fff',
    },
  },
};
const reportFuc = report();
engageLabCore.account.takeAwait().then((res: any) => {
  reportFuc.account(res.devKey || '', res.email);
});
const noNeedCoreRoutes = ['organization', 'ssoNoPermission'];
const route = useRoute();

// 计算是否应该隐藏头部
const shouldHideHeader = computed(() => {
  return noNeedCoreRoutes.includes(route.name as string);
});

// 控制头部显示的函数
const toggleHeader = (hide: boolean) => {
  const headerElement = document.getElementById('core-header-container');
  if (headerElement) {
    headerElement.style.display = hide ? 'none' : 'block';
  }
};

// 监听路由变化，动态控制头部显示
watch(
  () => route.name,
  () => {
    toggleHeader(shouldHideHeader.value);
  },
  { immediate: true },
);

// 组件挂载时设置初始状态
onMounted(() => {
  toggleHeader(shouldHideHeader.value);
});

// 组件卸载时恢复头部显示
onUnmounted(() => {
  toggleHeader(false);
});
watch(
  () => route.path,
  () => {
    reportFuc.pageVisit();
  },
  {
    immediate: true,
  },
);
const localeObj = { zh_CN, en_US, zh_TW, ja_JP, th_TH, es_ES, id_ID };
const loading = ref<boolean>(true);

const currentInfo = reactive(defCurrentData());

provide('currentInfo', currentInfo);
engageLabCore.organization.watch(({ id }) => {
  determineNewSms(id || '');
  if (id === null) {
    engageLabCore.account.takeAwait().then((res: any) => {
      loading.value = false;
      if (res.lastLoginType === 'SSO') {
        location.hash = '#/console/' + id + '/sso-no-permission';
        return;
      } else {
        // 当组织数量为0的时候都到注册引导页（第2步），不用分是否以前有创建过
        const localePath = locale === 'en_US' ? '' : `/${locale}`;
        location.href = localePath + '/accounts/add-user-info';
        return;
      }
    });
  } else {
    getUserPermission(id).then(({ data }) => {
      currentInfo.data = Object.assign(defCurrentData().data, data);
      if (location.hash.length < 3) {
        getService(id).then(({ data }) => {
          loading.value = false;
          const page = data.enableBusiness.length === 0 ? '/guid' : '/overview';
          location.hash = '#/console/' + id + page;
        });
      } else {
        loading.value = false;
      }
    });
  }
});

const { determineNewSms } = useNewSms();
determineNewSms();

if (!getCookie('cw_d_session_info')) {
  liveDeskAuth();
}

return (_ctx: any,_cache: any) => {
  const _component_RouterView = _resolveComponent("RouterView")!

  return (_openBlock(), _createBlock(_unref(ConfigProvider), {
    locale: localeObj[_unref(locale)],
    "prefix-cls": "jant",
    theme: themeConfig
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(Main), {
        class: _normalizeClass({ 'no-header': shouldHideHeader.value })
      }, {
        default: _withCtx(() => [
          (noNeedCoreRoutes.includes(_unref(route).name as string))
            ? (_openBlock(), _createBlock(_unref(AsyncComponents), {
                key: 0,
                loading: loading.value
              }, {
                default: _withCtx(() => [
                  _createElementVNode("main", _hoisted_1, [
                    _createVNode(_component_RouterView)
                  ])
                ]),
                _: 1
              }, 8, ["loading"]))
            : (_openBlock(), _createBlock(_unref(AsyncComponents), {
                key: 1,
                loading: loading.value
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Content))
                ]),
                _: 1
              }, 8, ["loading"]))
        ]),
        _: 1
      }, 8, ["class"])
    ]),
    _: 1
  }, 8, ["locale"]))
}
}

})